import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Bell, Check, CheckCheck } from "lucide-react";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { fmtDateTime } from "@/lib/dates";

export default function NotificationBell() {
  const [data, setData] = useState({ items: [], unread: 0 });
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const r = await api.get("/notifications", { params: { limit: 20 } });
      setData(r.data || { items: [], unread: 0 });
    } catch (e) { console.error("notifications/load failed:", e); }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [load]);

  const markRead = async (id) => {
    await api.patch(`/notifications/${id}/read`);
    load();
  };

  const markAll = async () => {
    await api.post("/notifications/read-all");
    load();
  };

  const onClick = async (n) => {
    await markRead(n.id);
    setOpen(false);
    if (n.link) navigate(n.link);
  };

  const kindColor = {
    project_assigned: "border-l-[#0a2540]",
    task_assigned: "border-l-[#0a2540]",
    project_stuck: "border-l-[#b45309]",
    leave_decided: "border-l-[#0f766e]",
    compliance_reminder: "border-l-[#b91c1c]",
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className="relative h-9 w-9 inline-flex items-center justify-center rounded-sm border border-slate-200 bg-white hover:border-[#0a2540]"
          data-testid="notification-bell"
          aria-label="Notifications"
        >
          <Bell size={16} className="text-slate-600" />
          {data.unread > 0 && (
            <span
              className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 inline-flex items-center justify-center text-[10px] font-bold text-white bg-[#b91c1c] rounded-full"
              data-testid="notif-unread-count"
            >
              {data.unread > 99 ? "99+" : data.unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={8}
        className="w-96 p-0 rounded-sm border-slate-200"
        data-testid="notif-popover"
      >
        <div className="flex items-center justify-between px-3 py-2 border-b border-slate-200">
          <div className="text-sm font-semibold" style={{ fontFamily: "Outfit" }}>Notifications</div>
          {data.unread > 0 && (
            <button
              onClick={markAll}
              className="text-[11px] text-[#0a2540] inline-flex items-center gap-1 hover:underline"
              data-testid="btn-mark-all-read"
            >
              <CheckCheck size={11} /> Mark all read
            </button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {data.items.length === 0 && (
            <div className="text-xs text-slate-400 text-center py-8">No notifications yet</div>
          )}
          {data.items.map((n) => (
            <div
              key={n.id}
              onClick={() => onClick(n)}
              className={`border-b border-slate-100 border-l-2 px-3 py-2.5 cursor-pointer hover:bg-slate-50 ${
                kindColor[n.kind] || "border-l-slate-300"
              } ${n.read ? "opacity-60" : ""}`}
              data-testid={`notif-${n.id}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-semibold text-[#0f172a]">{n.title}</div>
                  <div className="mt-0.5 text-[11px] text-slate-500 line-clamp-2 whitespace-pre-wrap">{n.body}</div>
                  <div className="mt-1 text-[10px] text-slate-400 font-mono-data">
                    {fmtDateTime(n.created_at)}
                  </div>
                </div>
                {!n.read && (
                  <button
                    onClick={(e) => { e.stopPropagation(); markRead(n.id); }}
                    className="text-slate-400 hover:text-[#0a2540]"
                    title="Mark read"
                  >
                    <Check size={12} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
